import { useState } from 'react';
import { P, match } from 'ts-pattern';

import SmallArtworkCard from 'components/SmallArtworkCard';
import TransactionModal from 'components/TransactionModal';
import InputEth from 'components/base/InputEth';
import Modal from 'components/base/Modal';
import AmountBreakDown from 'components/transactions/generic/AmountBreakdown';

import { useQueryEffects } from 'hooks/react-query';
import useModalVisibility from 'hooks/use-modal-visibility';
import { useGetMinBidAmount } from 'hooks/web3/use-get-min-bid-amount';
import usePlaceBid, {
  PLACE_BID_ACTION,
  ProposedBid,
} from 'hooks/web3/use-place-bid';
import useReferral from 'hooks/web3/use-referral';
import { ChainId } from 'lib/chains';
import { ZERO_ADDRESS } from 'lib/constants';
import { placeBidSchema } from 'schemas/forms/price';
import { ENV } from 'utils/env';
import { getTokenFilter } from 'utils/inputs';
import { extractErrorMessage } from 'utils/revert-reasons';
import { formatEther } from 'utils/units';

import { NftFilter } from 'types/Nft';
import { ModalOfType } from 'types/modal';

const MODAL_KEY = 'PLACE_BID';

type PlaceBidModalOptions = ModalOfType<typeof MODAL_KEY>;

export default function PlaceBidModal() {
  const modal = useModalVisibility(MODAL_KEY);

  return (
    <Modal.Root open={modal.open} onOpenChange={modal.onOpenChange}>
      <Modal.Portal>
        <Modal.BlurOverlay />
        <Modal.PositionOverlay>
          <Modal.UnmountListener onUnmount={modal.onUnmount} />
          {modal.config && <PlaceBidModalWindow {...modal.config} />}
        </Modal.PositionOverlay>
      </Modal.Portal>
    </Modal.Root>
  );
}

function PlaceBidModalWindow(props: PlaceBidModalOptions) {
  const [price, setPrice] = useState('');

  const { auctionId, showTxButton } = getNormalizedValues(props);

  const nftFilter = match(props)
    .with(
      {
        version: 1,
        artwork: { contractAddress: P.string, tokenId: P.number },
      },
      ({ artwork }): NftFilter => ({
        chainId: artwork.chainId as ChainId,
        contractAddress: artwork.contractAddress,
        tokenId: artwork.tokenId,
      })
    )
    .with(
      {
        version: 2,
      },
      ({ nft }): NftFilter => getTokenFilter(nft)
    )
    .otherwise(() => ({
      chainId: ENV.PRIMARY_CHAIN_ID,
      contractAddress: ZERO_ADDRESS,
      tokenId: 0,
    }));

  const referralAddress = useReferral({
    contractAddress: nftFilter.contractAddress,
  });

  const chainId = nftFilter.chainId;

  const placeBidArgs = placeBidSchema.safeParse({
    amount: price,
    referrer: referralAddress || ZERO_ADDRESS,
  });

  const getMinBidAmountQuery = useGetMinBidAmount({
    chainId,
    auctionId,
  });

  useQueryEffects(getMinBidAmountQuery, {
    onSuccess(minBidAmount) {
      setPrice(formatEther(minBidAmount));
    },
  });

  const proposedBid: ProposedBid | null = placeBidArgs.success
    ? {
        auctionId,
        amount: placeBidArgs.data.amount,
        referrer: placeBidArgs.data.referrer,
      }
    : null;

  const placeBid = usePlaceBid({
    nftFilter,
    proposedBid,
    worldId: null,
  });

  return (
    <TransactionModal
      chainId={chainId}
      preSignPrompt={{
        type: 'skip',
      }}
    >
      <TransactionModal.Content
        action={PLACE_BID_ACTION}
        chainId={chainId}
        txHash={placeBid.txHash}
        footer={
          <Modal.Footer>
            {showTxButton && (
              <TransactionModal.TransactionButton
                isDisabled={!placeBid.simulation.isSuccess}
                chainId={chainId}
                isLoading={placeBid.hasTxPrompt || Boolean(placeBid.txHash)}
                write={placeBid.prompt}
                error={extractErrorMessage({
                  simulateError: placeBid.simulation.error,
                  parsedZodSchema: placeBidArgs,
                })}
                label="Place bid"
              />
            )}
          </Modal.Footer>
        }
      >
        <TransactionModal.Body>
          <Modal.BodyTitle align="left" title="Place a bid" />

          {props.version === 1 ? (
            <SmallArtworkCard.Legacy
              artwork={props.artwork}
              activeAuction={props.auction}
            />
          ) : (
            <SmallArtworkCard.MarketNft
              activeAuction={props.auction}
              nft={props.nft}
            />
          )}
          <TransactionModal.InputWrapper>
            <InputEth
              id="price"
              value={price}
              onChange={(event) => {
                setPrice(event.target.value);
              }}
            />
            <AmountBreakDown.Available />
          </TransactionModal.InputWrapper>
        </TransactionModal.Body>
      </TransactionModal.Content>
    </TransactionModal>
  );
}

const getNormalizedValues = (options: PlaceBidModalOptions) => {
  if (options.version === 1) {
    const { artwork, auction } = options;

    return {
      auctionId: auction.auctionId,
      showTxButton: Boolean(artwork && artwork.contractAddress),
    };
  } else {
    const { nft } = options;

    return {
      auctionId: options.auction.id,
      showTxButton: Boolean(nft && nft.contractAddress),
    };
  }
};
